// src/components/ConceptoAutocomplete.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  IconButton,
  Divider,
  Typography,
  Paper
} from '@mui/material';
import { Star as StarIcon, StarBorder as StarBorderIcon } from '@mui/icons-material';
import { useSftfApi } from '../api/sftfClient';
import { useAuth } from '../context/AuthContext';
import { construirColoresPorProducto } from '../config/conceptoAutocompleteColors';
import { ALLOWED_BILLING_CONCEPTS, codigosPermitidosInexistentes } from '../config/allowedBillingConcepts';
import { mapChemicalToProductCode, productosSinSinonimos } from '../config/mapChemicalToProductCode';

// Función auxiliar para ordenar opciones, agrupando por favoritos globales primero y luego por Producto
const sortOptions = (opts, favs) => {
  return [...opts].sort((a, b) => {
    // 1. Priorizamos Favoritos Globalmente para que todo quede en un solo grupo arriba
    const aFav = favs.includes(a.CodigoGasto);
    const bFav = favs.includes(b.CodigoGasto);

    if (aFav && !bFav) return -1;
    if (!aFav && bFav) return 1;

    // 2. Si es empate (ambos son favs o ninguno lo es), ordenamos por categoría de "Producto"
    const prodA = a.Producto || 'OTROS';
    const prodB = b.Producto || 'OTROS';

    if (prodA < prodB) return -1;
    if (prodA > prodB) return 1;

    // 3. Finalmente, ordenamos alfabéticamente por CodigoGasto
    return (a.CodigoGasto || '').localeCompare(b.CodigoGasto || '');
  });
};

const ConceptoAutocomplete = ({
  name,
  label,
  value,
  onChange,
  onBlur,
  error,
  touched,
  fullWidth = true,
  disabled = false,
  size = "medium",
  filterAllowed = true,
  externalSearchTerm = ""
}) => {
  const { auth } = useAuth();
  const { buscarConceptos } = useSftfApi(auth);
  const [options, setOptions] = useState([]);
  const [rawOptions, setRawOptions] = useState([]);
  // Copia sin filtrar del catálogo, tal como lo devuelve ConceptoGasto/Consultar.
  // A diferencia de rawOptions/options (que sólo contienen los códigos curados
  // en ALLOWED_BILLING_CONCEPTS cuando internalFilter está activo), este estado
  // se usa para derivar los colores por producto y para validar la lista
  // curada y el mapa de sinónimos contra el catálogo real.
  //
  // Sólo se llena desde las cargas SIN texto de búsqueda (carga inicial e
  // input vacío), que llaman a buscarConceptos(''). buscarConceptos filtra
  // del lado del cliente por el texto recibido, así que una búsqueda con
  // texto (buscarConceptos(newInputValue)) devuelve un subconjunto angosto,
  // no el catálogo — asignarlo aquí haría que codigosPermitidosInexistentes
  // reporte falsos positivos en cada tecleo. Ver handleInputChange.
  const [catalogoCompleto, setCatalogoCompleto] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [internalFilter, setInternalFilter] = useState(filterAllowed);
  const [implicitProductFilter, setImplicitProductFilter] = useState('');

  const coloresPorProducto = React.useMemo(
    () => construirColoresPorProducto(catalogoCompleto),
    [catalogoCompleto]
  );

  // Reaccionar a términos de búsqueda externos implícitos (ej. extraer gas desde XML y filtrar el dropdown)
  useEffect(() => {
    if (externalSearchTerm && !value) {
      const cleanTerm = mapChemicalToProductCode(externalSearchTerm);
      setImplicitProductFilter(cleanTerm);
    }
  }, [externalSearchTerm, value]);

  // Sincronizar inputValue con el valor seleccionado solo cuando cambia
  const prevValueRef = React.useRef(value);
  useEffect(() => {
    if (value !== prevValueRef.current) {
      if (value) {
        const label = typeof value === 'string'
          ? value
          : `${value.CodigoGasto} - ${value.Descripcion}`;
        setInputValue(label);
      } else if (value === null || value === undefined) {
        setInputValue('');
      }
      prevValueRef.current = value;
    }
  }, [value]);

  // Manejo de Favoritos en LocalStorage
  const [favorites, setFavorites] = useState(() => {
    try {
      const saved = localStorage.getItem('conceptos_favorites');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const toggleFavorite = (event, codigoGasto) => {
    event.stopPropagation(); // Evitar que seleccione la opción al hacer click en la estrella
    let newFavorites;
    if (favorites.includes(codigoGasto)) {
      newFavorites = favorites.filter(fav => fav !== codigoGasto);
    } else {
      newFavorites = [...favorites, codigoGasto];
    }
    setFavorites(newFavorites);
    localStorage.setItem('conceptos_favorites', JSON.stringify(newFavorites));
  };

  const filterConceptos = useCallback((conceptos) => {
    const arr = Array.isArray(conceptos) && conceptos.length > 0 ? conceptos : [];
    if (!internalFilter) return arr;
    return arr.filter(c => ALLOWED_BILLING_CONCEPTS.includes(c.CodigoGasto));
  }, [internalFilter]);

  const activeSearchRef = React.useRef(0);

  // Cargar conceptos iniciales con fallback
  useEffect(() => {
    let unmounted = false;
    const loadInitialConceptos = async () => {
      const currentSearchId = ++activeSearchRef.current;
      try {
        setLoading(true);
        const conceptos = await buscarConceptos('');
        
        if (unmounted || activeSearchRef.current !== currentSearchId) {
          console.log('[ConceptoAutocomplete] Descartando carga inicial, búsqueda más reciente activa.');
          return;
        }

        const conceptosArray = filterConceptos(conceptos);
        setRawOptions(conceptosArray);
        setCatalogoCompleto(Array.isArray(conceptos) ? conceptos : []);
      } catch (err) {
        console.error('Error al cargar conceptos iniciales:', err);
        setRawOptions([]);
        // catalogoCompleto se deja tal cual (no se limpia): conserva el
        // último catálogo completo cargado con éxito, que sigue siendo un
        // dato útil para el diagnóstico. Limpiarlo aquí sólo volvería a
        // disparar el estado "aún no cargó" y apagaría el aviso sin razón.
      } finally {
        if (!unmounted && activeSearchRef.current === currentSearchId) {
          setLoading(false);
        }
      }
    };

    loadInitialConceptos();

    return () => {
      unmounted = true;
    };
  }, [buscarConceptos, filterConceptos]);

  // Buscar conceptos cuando el usuario escribe con manejo de fallback
  const handleInputChange = async (event, newInputValue) => {
    setInputValue(newInputValue);
    const currentSearchId = ++activeSearchRef.current;

    // Buscar conceptos si hay texto de búsqueda
    if (newInputValue.length >= 1) {
      try {
        setLoading(true);
        const conceptos = await buscarConceptos(newInputValue);
        
        if (activeSearchRef.current !== currentSearchId) return;

        const conceptosArray = filterConceptos(conceptos);
        setRawOptions(conceptosArray);
        // catalogoCompleto NO se actualiza aquí: buscarConceptos(newInputValue)
        // devuelve sólo lo que matchea el texto tecleado, no el catálogo completo.
      } catch (err) {
        console.error('Error al buscar conceptos:', err);
        if (activeSearchRef.current === currentSearchId) {
          setRawOptions([]);
          // catalogoCompleto se deja tal cual (no se limpia): conserva el
          // último catálogo completo cargado con éxito, que sigue siendo un
          // dato útil para el diagnóstico. Limpiarlo aquí sólo volvería a
          // disparar el estado "aún no cargó" y apagaría el aviso sin razón.
        }
      } finally {
        if (activeSearchRef.current === currentSearchId) {
          setLoading(false);
        }
      }
    } else if (newInputValue.length === 0) {
      // Si el campo está vacío, cargar todas las opciones
      try {
        setLoading(true);
        const conceptos = await buscarConceptos('');
        
        if (activeSearchRef.current !== currentSearchId) return;

        const conceptosArray = filterConceptos(conceptos);
        setRawOptions(conceptosArray);
        setCatalogoCompleto(Array.isArray(conceptos) ? conceptos : []);
      } catch (err) {
        console.error('Error al cargar conceptos:', err);
        if (activeSearchRef.current === currentSearchId) {
          setRawOptions([]);
          // catalogoCompleto se deja tal cual (no se limpia): conserva el
          // último catálogo completo cargado con éxito, que sigue siendo un
          // dato útil para el diagnóstico. Limpiarlo aquí sólo volvería a
          // disparar el estado "aún no cargó" y apagaría el aviso sin razón.
        }
      } finally {
        if (activeSearchRef.current === currentSearchId) {
          setLoading(false);
        }
      }
    }
  };

  const handleChange = (event, newValue) => {
    console.log(`\n================================`);
    console.log(`[ConceptoAutocomplete] Campo: ${name}`);
    console.log(`Objeto devuelto por la API completo:`);
    console.dir(newValue, { depth: null });
    console.log(`JSON Format:\n${JSON.stringify(newValue, null, 2)}`);
    console.log(`================================\n`);
    onChange(name, newValue);
  };

  // Re-ordenar opciones si cambian los favoritos o las opciones base
  useEffect(() => {
    let finalOptions = rawOptions;
    // Aplicar el filtro dinámico e invisible de producto si fue llenado por XML
    // Omitir este filtro si el usuario decide "Mostrar todos los conceptos" (internalFilter = false)
    if (implicitProductFilter && internalFilter) {
      finalOptions = finalOptions.filter(c => c.Producto === implicitProductFilter);
    }
    setOptions(sortOptions(finalOptions, favorites));
  }, [rawOptions, favorites, implicitProductFilter, internalFilter]);

  // Avisar en consola cuando la lista curada o el mapa de sinónimos se
  // desalinean del catálogo real. Se revisa contra catalogoCompleto (sin
  // filtrar), no contra options/rawOptions, para detectar productos y
  // códigos que el filtro por ALLOWED_BILLING_CONCEPTS oculta del dropdown.
  useEffect(() => {
    if (!Array.isArray(catalogoCompleto) || catalogoCompleto.length === 0) return;

    const muertos = codigosPermitidosInexistentes(ALLOWED_BILLING_CONCEPTS, catalogoCompleto);
    if (muertos.length > 0) {
      console.warn(
        'Conceptos de la lista por defecto que ya no existen en el catálogo:',
        muertos.join(', ')
      );
    }

    const sinSinonimos = productosSinSinonimos(catalogoCompleto);
    if (sinSinonimos.length > 0) {
      console.warn(
        'Productos del catálogo sin sinónimos en mapChemicalToProductCode:',
        sinSinonimos.join(', ')
      );
    }
  }, [catalogoCompleto]);

  return (
    <Autocomplete
      options={Array.isArray(options) ? options : []}
      value={value}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      onBlur={onBlur}
      sx={{
        minHeight: '56px',
        height: '56px',
        minWidth: '200px',
        '& .MuiOutlinedInput-root': {
          height: '56px'
        },
        '& .MuiOutlinedInput-notchedOutline': {
          minHeight: '56px'
        }
      }}
      disabled={disabled}
      fullWidth={fullWidth}
      loading={loading}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option ? `${option.CodigoGasto} - ${option.Descripcion}` : '';
      }}
      filterOptions={(x) => x}
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) return false;
        return option.CodigoGasto === value.CodigoGasto;
      }}
      groupBy={(option) => favorites.includes(option.CodigoGasto) ? `⭐ FAVORITOS - ${option.Producto || 'OTROS'}` : (option.Producto || 'OTROS')}
      PaperComponent={({ children, ...paperProps }) => {
        return (
          <Paper {...paperProps}>
            <Box sx={{ p: 1, borderBottom: '1px solid rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', backgroundColor: 'background.paper' }}>
              <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
                Mostrar todos los conceptos
              </Typography>
              <input 
                type="checkbox" 
                checked={!internalFilter} 
                onChange={(e) => setInternalFilter(!e.target.checked)} 
                onMouseDown={(e) => e.preventDefault()} // Para evitar que se cierre el menú de opciones
                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
              />
            </Box>
            {children}
          </Paper>
        );
      }}
      renderGroup={(params) => (
        <React.Fragment key={params.key}>
          <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
            <Typography variant="caption" color="primary" fontWeight="bold">
              {params.group}
            </Typography>
          </Box>
          <Divider sx={{ mb: 1 }} />
          {params.children}
        </React.Fragment>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={touched && Boolean(error)}
          helperText={touched && error}
          size={size}
          sx={{
            '& .MuiInputBase-root': {
              height: '56px'
            },
            '& .MuiInputBase-input': {
              height: '56px',
              display: 'flex',
              alignItems: 'center'
            }
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <Box>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </Box>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        // Validar que option existe y es un objeto
        if (!option || typeof option !== 'object') {
          const { key, ...otherProps } = props;
          return (
            <Box key={key} component="li" {...otherProps}>
              <Box>Opción inválida</Box>
            </Box>
          );
        }

        const isFavorite = favorites.includes(option.CodigoGasto);
        const bgColor = coloresPorProducto[option.Producto] || 'inherit';

        const { key, ...otherProps } = props;

        return (
          <Box
            key={key}
            component="li"
            {...otherProps}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: bgColor + ' !important', // overrides default MUI 'transparent' that blocks it
              borderBottom: '1px solid rgba(0,0,0,0.03)',
              '&:hover': {
                filter: 'brightness(0.95)'
              }
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={{ fontWeight: 'bold', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                {option.CodigoGasto || 'Sin código'}
                {isFavorite && <StarIcon color="warning" fontSize="small" />}
              </Box>
              <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', mt: 0.5 }}>
                {option.Descripcion || 'Sin descripción'}
              </Box>
              <Box sx={{ fontSize: '0.75rem', color: 'text.disabled', mt: 0.5 }}>
                {option.Producto && option.TipoUnidad ?
                  `${option.Producto} - ${option.TipoUnidad}` :
                  option.Producto || option.TipoUnidad || 'N/A'
                }
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => toggleFavorite(e, option.CodigoGasto)}
              color={isFavorite ? "warning" : "default"}
            >
              {isFavorite ? <StarIcon /> : <StarBorderIcon />}
            </IconButton>
          </Box>
        );
      }}
      noOptionsText="No se encontraron conceptos"
      loadingText="Buscando conceptos..."
    />
  );
};

export default ConceptoAutocomplete;